import { useEffect, useState } from 'react';
import { adminAuthHeaders, api } from '../lib/api';
import type { AdminResourceOptions, AdminResourceState } from '../types/contracts';

export function useAdminResource<T>(path: string, options: AdminResourceOptions<T> = {}): AdminResourceState<T> {
  const {
    enabled = true,
    initialData,
    transform = (value: unknown) => value as T,
    onError,
  } = options;

  const [data, setData] = useState<T>(initialData as T);
  const [loading, setLoading] = useState(Boolean(enabled));

  const refresh = async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const response = await api.get(path, adminAuthHeaders());
      setData(transform(response.data) as T);
    } catch (error) {
      console.error(`Error fetching ${path}:`, error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    refresh();
    // `transform` and `onError` are passed as stable inline wrappers from the caller's render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled]);

  return { data, setData, loading, refresh };
}
